/**
 * SYM-6206. Which player the preview pane shows.
 *
 * Three states, and the order between them is the whole point:
 *
 * - `plan`       — the draft is one OUR renderer will make, so the pane plays
 *                  the pictures ffmpeg will composite.
 * - `creatomate` — no plan, but a Creatomate composition and a token: what the
 *                  wizard has always done, untouched.
 * - `placeholder`— neither, so the pane says why instead of sitting black.
 *
 * ⚠️ A PLAN WINS EVEN WHEN THE SDK IS UNHAPPY. The Creatomate hook sets an error
 * as soon as there is no public token ("Preview requires Creatomate Growth
 * plan"), which has nothing to do with our player -- gating our pane on that
 * error would hide a working preview behind a complaint about a service it does
 * not use.
 *
 * It lives in its own file because the wizard has no component tests: inside the
 * JSX this rule would be three conditions nobody can exercise, and "the preview
 * pane went blank" is exactly the kind of fault that reaches a customer.
 */
export type PanePlayer = 'plan' | 'creatomate' | 'placeholder';

export interface PaneChoice {
  hasPlan: boolean;
  hasCreatomateToken: boolean;
  sdkError: string | null;
}

export const choosePlayer = ({
  hasPlan,
  hasCreatomateToken,
  sdkError,
}: PaneChoice): PanePlayer => {
  if (hasPlan) return 'plan';

  if (hasCreatomateToken && !sdkError) return 'creatomate';

  return 'placeholder';
};

/**
 * The error the pane should act on.
 *
 * Null whenever our own player is driving: the SDK's complaints are about a
 * service that is not being used.
 */
export const paneError = (
  hasPlan: boolean,
  sdkError: string | null
): string | null => (hasPlan ? null : sdkError);
